import React, { useCallback, useMemo } from "react";
import { connect } from "react-redux";

import { EntityType } from "@mapmycustomers/shared/enum";
import FieldFeature from "@mapmycustomers/shared/enum/fieldModel/FieldFeature";
import ColumnModel from "@mapmycustomers/shared/types/viewModel/internalModel/ColumnModel";
import FilterModel from "@mapmycustomers/shared/types/viewModel/internalModel/FilterModel";
import { RegularSavedFilter } from "@mapmycustomers/shared/types/viewModel/SavedFilter";
import { isDefined } from "@mapmycustomers/shared/util/assert";

import DownloadEntitiesButton from "@app/component/DownloadEntitiesModal/DownloadEntitiesButton";
import {
  FrequencyStatusFilter,
  NearbyFilter,
  NextActivityFilter,
  NoLocationFilter,
  NoRecentContactFilter,
  RecentlyAddedFilter,
  StandardFilterButton,
} from "@app/component/filtering";
import FrequencyFilter from "@app/component/filtering/components/FrequencyFilter";
import QuickFilterBar from "@app/component/QuickFilterBar";
import SelectColumnsButton from "@app/component/SelectColumnsPane/SelectColumnsButton";
import FieldFilter from "@app/component/SelectColumnsPane/type/FieldFilter";
import { skipNonListViewFields } from "@app/component/SelectColumnsPane/util/fieldFilter";
import useView from "@app/component/view/utils/useView";
import { isCurrentUserMember } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import { getFavoriteSavedFilters } from "@app/store/savedFilters";
import companyFieldModel from "@app/util/fieldModel/CompanyFieldModel";

import { downloadList, setOfferedFilters } from "../store/actions";
import {
  getListViewState,
  getOfferedFilters,
  getTotalFilteredRecordsCount,
} from "../store/selectors";

import styles from "./CompanyListToolbar.module.scss";

interface Props {
  columns: ColumnModel;
  downloadList: typeof downloadList;
  favoriteSavedFilters: RegularSavedFilter[];
  isMember?: boolean;
  offerFilters?: Partial<FilterModel>;
  onSetCustomFilters: typeof setOfferedFilters;
  totalFilteredRecordsCount: number;
}

const CompanyListToolbar: React.FC<Props> = ({
  columns,
  downloadList,
  favoriteSavedFilters,
  isMember,
  offerFilters,
  onSetCustomFilters,
  totalFilteredRecordsCount,
}) => {
  const {
    updateFilterModel,
    viewState: { filter },
  } = useView();

  const hiddenFilterFields = useMemo(
    () =>
      companyFieldModel.fields
        .filter(
          (field) =>
            (field.hasFeature(FieldFeature.NON_LIST_VIEW) &&
              !field.hasAnyFeature(FieldFeature.VIEW_AS_FIELD, FieldFeature.TEAM_FIELD)) ||
            // viewAs filter should not be available for members
            (isMember && field.hasFeature(FieldFeature.VIEW_AS_FIELD))
        )
        .filter(isDefined),
    [isMember]
  );

  const handledDownload = useCallback(
    (columns: ColumnModel) => {
      downloadList({ columns });
    },
    [downloadList]
  );

  const fieldFilter: FieldFilter = useCallback(
    // TODO: should we even check against isReadable here?
    (field) => skipNonListViewFields(field) && (!!field.isReadable || !!field.isSystemRequired),
    []
  );

  const handleClearOfferFilters = useCallback(
    () => onSetCustomFilters(undefined),
    [onSetCustomFilters]
  );

  return (
    <div className={styles.container}>
      <div className={styles.leftContainer}>
        <StandardFilterButton
          context="companyPage"
          entityType={EntityType.COMPANY}
          hiddenFields={hiddenFilterFields}
          offerFilters={offerFilters}
          onHidePopover={handleClearOfferFilters}
        />
        <QuickFilterBar savedFilters={favoriteSavedFilters}>
          <NearbyFilter
            filter={filter}
            onSetCustomFilters={onSetCustomFilters}
            onUpdateFilterModel={updateFilterModel}
          />
          <NoRecentContactFilter
            filter={filter}
            onSetCustomFilters={onSetCustomFilters}
            onUpdateFilterModel={updateFilterModel}
          />
          <FrequencyFilter
            filter={filter}
            onSetCustomFilters={onSetCustomFilters}
            onUpdateFilterModel={updateFilterModel}
          />
          <FrequencyStatusFilter
            filter={filter}
            onSetCustomFilters={onSetCustomFilters}
            onUpdateFilterModel={updateFilterModel}
          />
          <RecentlyAddedFilter
            filter={filter}
            onSetCustomFilters={onSetCustomFilters}
            onUpdateFilterModel={updateFilterModel}
          />
          <NextActivityFilter
            filter={filter}
            onSetCustomFilters={onSetCustomFilters}
            onUpdateFilterModel={updateFilterModel}
          />
          <NoLocationFilter filter={filter} onUpdateFilterModel={updateFilterModel} />
        </QuickFilterBar>
      </div>
      <div className={styles.rightContainer}>
        <SelectColumnsButton entityType={EntityType.COMPANY} fieldFilter={fieldFilter} />
        <DownloadEntitiesButton
          defaultColumnList={columns}
          entityType={EntityType.COMPANY}
          onDownload={handledDownload}
          total={totalFilteredRecordsCount}
        />
      </div>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  columns: getListViewState(state).columns,
  favoriteSavedFilters: getFavoriteSavedFilters(state)(EntityType.COMPANY) as RegularSavedFilter[],
  isMember: isCurrentUserMember(state),
  offerFilters: getOfferedFilters(state),
  totalFilteredRecordsCount: getTotalFilteredRecordsCount(state),
});

const mapDispatchToProps = {
  downloadList,
  onSetCustomFilters: setOfferedFilters,
};

export default connect(mapStateToProps, mapDispatchToProps)(CompanyListToolbar);
